'use client';

import { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Link } from '@/i18n/navigation';
import styles from './LegalSummary.module.css';

export interface LegalSummaryItem {
  label: string;
  value?: string;
  description?: string;
  icon?: string;
  href?: string;
}

interface LegalSummaryProps {
  items: LegalSummaryItem[];
  variant?: 'facts' | 'cards' | 'links';
  ariaLabel?: string;
}

const EASE = [0.22, 1, 0.36, 1] as const;

const listVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.07, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
};

export function LegalSummary({
  items,
  variant = 'facts',
  ariaLabel,
}: LegalSummaryProps) {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  function renderContent(item: LegalSummaryItem) {
    return (
      <>
        {item.icon ? (
          <span className={`material-symbols-outlined ${styles.icon}`} aria-hidden>
            {item.icon}
          </span>
        ) : null}
        <div className={styles.text}>
          <span className={styles.label}>{item.label}</span>
          {item.value ? <span className={styles.value}>{item.value}</span> : null}
          {item.description ? (
            <span className={styles.description}>{item.description}</span>
          ) : null}
        </div>
      </>
    );
  }

  function itemCls(item: LegalSummaryItem) {
    return `${styles.item} ${item.href ? styles.linkItem : ''}`;
  }

  const sectionCls = `${styles.summary} ${styles[variant]}`;

  /* Server + reduced motion: render static without animation state */
  if (!mounted || reduced) {
    return (
      <section className={sectionCls} aria-label={ariaLabel}>
        <div className={styles.list}>
          {items.map((item, index) => {
            if (!item.href) return <div key={index} className={itemCls(item)}>{renderContent(item)}</div>;
            if (item.href.startsWith('#')) return <a key={index} href={item.href} className={itemCls(item)}>{renderContent(item)}</a>;
            return <Link key={index} href={item.href as any} className={itemCls(item)}>{renderContent(item)}</Link>;
          })}
        </div>
      </section>
    );
  }

  /* Client: stagger items in on scroll */
  return (
    <section className={sectionCls} aria-label={ariaLabel}>
      <motion.div
        className={styles.list}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        variants={listVariants}
      >
        {items.map((item, index) => {
          if (!item.href) {
            return (
              <motion.div key={index} className={itemCls(item)} variants={itemVariants}>
                {renderContent(item)}
              </motion.div>
            );
          }
          if (item.href.startsWith('#')) {
            return (
              <motion.a key={index} href={item.href} className={itemCls(item)} variants={itemVariants}>
                {renderContent(item)}
              </motion.a>
            );
          }
          /* Next.js Link: wrap in motion.div that is the grid cell */
          return (
            <motion.div key={index} className={itemCls(item)} variants={itemVariants}>
              <Link href={item.href as any} className={styles.linkFill}>
                {renderContent(item)}
              </Link>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}

